"use client"

import type {
  GenericsComponent,
  ThemeProps,
  WithoutThemeProps,
} from "@/components/ui"
import type { UseInputBorderProps } from "@/components/ui/components/input"
import type { IconButtonProps } from "@yamada-ui/react/components/button"
import type { ReactElement } from "react"
import type { ToggleStyle } from "./toggle.style"
import type { UseToggleProps } from "./use-toggle"
import { IconButton } from "@yamada-ui/react/components/button"
import { Portal } from "@yamada-ui/react/components/portal"
import { createSlotComponent, styled } from "@/components/ui"
import { useInputBorder } from "@/components/ui/components/input"
import { toggleStyle } from "./toggle.style"
import { useToggle } from "./use-toggle"

export interface ToggleProps<Y extends string = string>
  extends Omit<
      WithoutThemeProps<IconButtonProps, ToggleStyle>,
      "aria-label" | "onChange" | "ref" | "value"
    >,
    UseToggleProps<Y>,
    Pick<UseInputBorderProps, "errorBorderColor">,
    ThemeProps<ToggleStyle> {}

const {
  component,
  PropsContext: TogglePropsContext,
  usePropsContext: useTogglePropsContext,
  withProvider,
  useRootComponentProps,
} = createSlotComponent<ToggleProps, ToggleStyle>("toggle", toggleStyle)

export {
  component,
  TogglePropsContext,
  useRootComponentProps,
  useTogglePropsContext,
}

/**
 * `Toggle` is a two-state button that can be either on or off.
 *
 * @see https://yamada-ui.com/docs/components/toggle
 */
export const Toggle = withProvider<"button", ToggleProps>(
  ({ errorBorderColor, icon, ...rest }: ToggleProps) => {
    const { getButtonProps, getInputProps } = useToggle(rest)
    const varProps = useInputBorder({ errorBorderColor })

    return (
      <>
        <Portal>
          <styled.input {...getInputProps()} />
        </Portal>

        <IconButton icon={icon} {...varProps} {...getButtonProps()} />
      </>
    )
  },
  "root",
)() as GenericsComponent<{
  <Y extends string = string>(props: ToggleProps<Y>): ReactElement
}>
